import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  TextField,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Tooltip,
} from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@apollo/client';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { parseISO, isWithinInterval, format } from 'date-fns';

import { GET_WALLET_SUMMARY, GET_COIN_TRANSACTIONS } from '../../graphql/queries/tip';
import { UserContext } from '../auth/auth-context';
import PaymentSettings from '../../pages/customer/booking/paymentSettings';
import { MIN_COINS_TO_WITHDRAW } from '../../util/constants';
// Helper: parse ISO string or numeric timestamp
const parseDate = (val) =>
  typeof val === 'string' && /^\d+$/.test(val)
    ? new Date(Number(val))
    : parseISO(val);

export default function WalletDashboardInner() {
  const navigate = useNavigate();
  const { businessUrl, staffUrl } = useParams();
  const { providerUser } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State: date filters, calculations, copy status
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCalc, setShowCalc] = useState(false);
  const [copied, setCopied] = useState(false);
  const [didWithdraw, setDidWithdraw] = useState(false);

  // State: menu and settings dialog
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [settingsOpen, setSettingsOpen] = useState(false);
 const location = useLocation();
  // Queries: summary & transactions
  const { data: sumData, loading: sumLoading, refetch: refetchSummary } = useQuery(
    GET_WALLET_SUMMARY,
    { variables: { userId: providerUser?.userId }, skip: !providerUser?.userId }
  );
  const { data: txData, loading: txLoading, refetch: refetchTx } = useQuery(
    GET_COIN_TRANSACTIONS,
    { variables: { userId: providerUser?.userId }, skip: !providerUser?.userId }
  );

  // Guard: ensure logged-in creator
  const isCreator = providerUser?.url === staffUrl;
  useEffect(() => {
    if (!providerUser?.userId) {
      navigate('/login');
    } else if (!isCreator) {
      navigate(`/${businessUrl}/${providerUser.url}/wallet`);
    }
  }, [providerUser, isCreator, businessUrl, navigate]);

 

  useEffect(() => {
    if (location.state?.didWithdraw) {
      refetchSummary();
      refetchTx();
        setDidWithdraw(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state,didWithdraw, refetchSummary, refetchTx, navigate, location.pathname]);

  if (!providerUser?.userId || !isCreator) return null;

  // Loading state
  if (sumLoading || txLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Destructure summary
  const { balance, totalPurchased, totalSpent, tipsReceived, tipsSent } = sumData?.getWalletSummary;
  const transactions = txData?.getPlanyCoinTransactions;

  // Date filter helper
  const filterByDate = (list) =>
    !startDate || !endDate
      ? list
      : list?.filter((t) => {
        const d = parseDate(t.createdAt);
        return isWithinInterval(d, {
          start: parseDate(startDate),
          end: parseDate(endDate),
        });
      });

  // Partition transactions
  const purchases = filterByDate(transactions?.filter((t) => t.type === 'purchase'));
  const cashOuts = filterByDate(transactions?.filter((t) => t.type === 'withdraw'));
  const allTips = filterByDate(transactions?.filter((t) => t.type === 'tip'));
  const receivedTx = allTips?.filter((tx) => Number(tx.targetUser?.id) === Number(providerUser.userId));
  const sentTx = allTips?.filter((tx) => Number(tx.user?.id) === Number(providerUser.userId));

  // Tip page URL copy
  const tipPageUrl = `/${businessUrl}/${staffUrl}/tip`;
  const fullTipUrl = `${window.location.origin}${tipPageUrl}`;
  const copyUrl = () => {
    navigator.clipboard.writeText(fullTipUrl)
      .then(() => setCopied(true))
      .finally(() => setTimeout(() => setCopied(false), 2000));
  };

  // Menu handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleSettingsOpen = () => {
    setSettingsOpen(true);
    handleMenuClose();
  };
  const handleSettingsClose = () => setSettingsOpen(false);

  // Table styling
  const colWidths = { date: '80px', user: '100px', coins: '60px' };
  const tableSx = { borderCollapse: 'collapse', tableLayout: 'fixed', width: 'auto', '& th, & td': { padding: '6px', whiteSpace: 'nowrap' } };

  return (
    <Box p={isMobile ? 2 : 3}>
      {/* Header with menu */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">🪙 Your PlanyCoin Wallet</Typography>
        <IconButton onClick={handleMenuOpen} size="large">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleSettingsOpen}>PayPal Settings</MenuItem>
          <MenuItem onClick={() => { navigate('/coins/buy'); handleMenuClose(); }}>Buy Coins</MenuItem>
          <MenuItem onClick={() => { navigate(`/${businessUrl}/${staffUrl}/withdraw`); handleMenuClose(); }} disabled={balance < MIN_COINS_TO_WITHDRAW}>
            Cash Out to PayPal
          </MenuItem>
        </Menu>
      </Box>

      {/* Summary Panel */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">💰 Balance: {balance} PlanyCoins</Typography>
            <Button size="small" onClick={() => setShowCalc(!showCalc)} sx={{ mt: 1 }}>
              {showCalc ? 'Hide Calculation' : 'Show Calculation'}
            </Button>
            {showCalc && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                Purchased ({totalPurchased}) + Received ({tipsReceived}) - Sent ({tipsSent}) - Cash Out ({totalSpent})
              </Typography>
            )}
          </Box>
          <Box mt={isMobile ? 2 : 0}>
            <Button variant="contained" color="secondary" component={Link} to="/coins/buy" sx={{ mr: isMobile ? 0 : 2 }}>
              💸 Buy Coins
            </Button>
            <Tooltip title={balance < MIN_COINS_TO_WITHDRAW ? `You don't have enough coins to cash out. Minimum is ${MIN_COINS_TO_WITHDRAW} coins.` : ''}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={balance < MIN_COINS_TO_WITHDRAW}
                  onClick={() => navigate(`/${businessUrl}/${staffUrl}/withdraw`)}
                >
                  📤 Cash Out
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box mt={2}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Your Tip Page URL:</Typography>
          <Box display="flex" alignItems="center" sx={{ wordBreak: 'break-all' }}>
            <MuiLink component={Link} to={tipPageUrl} sx={{ mr: 1, fontWeight: 500 }}>{fullTipUrl}</MuiLink>
            <IconButton size="small" onClick={copyUrl}>
              {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Date Filter */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>📆 Filter by Date</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <TextField type="date" label="End Date" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Box>
      </Paper>

      {/* Transactions... (existing tables remain unchanged) */}
      {/* Purchases Table */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>📦 Coins Purchased</Typography>
        <Divider sx={{ mb: 2 }} />
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: colWidths.date }}>Date</TableCell>
              <TableCell sx={{ width: colWidths.coins }}>Coins</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchases?.map(tx => (
              <TableRow key={tx.id}>
                <TableCell sx={{ width: colWidths.date }}>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                <TableCell sx={{ width: colWidths.coins }}>{tx.amount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Tips Received Table */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>📥 Tips Received</Typography>
        <Divider sx={{ mb: 2 }} />
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: colWidths.date }}>Date</TableCell>
              <TableCell sx={{ width: colWidths.user }}>From</TableCell>
              <TableCell sx={{ width: colWidths.coins }}>Coins</TableCell>
              <TableCell sx={{ width: colWidths.coins }}>Reason</TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {receivedTx?.map(tx => (
              <TableRow key={tx.id}>
                <TableCell sx={{ width: colWidths.date }}>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                <TableCell sx={{ width: colWidths.user }}>{tx.user.username}</TableCell>
                <TableCell sx={{ width: colWidths.coins }}>{tx.amount}</TableCell>
                <TableCell sx={{ width: colWidths.coins }}>{tx.note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Tips Sent Table */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>🎁 Tips Sent</Typography>
        <Divider sx={{ mb: 2 }} />
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: colWidths.date }}>Date</TableCell>
              <TableCell sx={{ width: colWidths.user }}>To</TableCell>
              <TableCell sx={{ width: colWidths.coins }}>Coins</TableCell>
              <TableCell sx={{ width: colWidths.coins }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sentTx?.map(tx => (
              <TableRow key={tx.id}>
                <TableCell sx={{ width: colWidths.date }}>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                <TableCell sx={{ width: colWidths.user }}>{tx.targetUser.username}</TableCell>
                <TableCell sx={{ width: colWidths.coins }}>{tx.amount}</TableCell>
                <TableCell sx={{ width: colWidths.coins }}>{tx.note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Cash Out Table */}
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>📤 Cash Out</Typography>
        <Divider sx={{ mb: 2 }} />
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: colWidths.date }}>Date</TableCell>
              <TableCell sx={{ width: colWidths.coins }}>Coins</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cashOuts?.map(tx => (
              <TableRow key={tx.id}>
                <TableCell sx={{ width: colWidths.date }}>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                <TableCell sx={{ width: colWidths.coins }}>{Math.abs(tx.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={handleSettingsClose} fullWidth maxWidth="md">
        <DialogTitle>Business Integration Settings</DialogTitle>
        <DialogContent dividers>
          {/* Render your business integration settings component here */}
          <PaymentSettings isDisabled={false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSettingsClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
